import React, { useState } from "react";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton, Tabs } from "../hrComponents";
import { cardStyle, formatDate, formatMinutes, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad, addDaysYmd } from "../hrUi";

// OT-01..06: overtime found from attendance (or asked for first, depending on the company's policy), and what is approved.
function OtList({ mine }) {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useLoad(() => hrmsService.listOvertime({ mine: mine ? 1 : undefined, status: status || undefined }), [mine, status]);
  const rows = data || [];
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        {[["", "All"], ["PENDING", "Waiting"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"]].map(([v, label]) => (
          <button key={v} type="button" className={`btn btn-sm ${status === v ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => setStatus(v)}>
            {label}
          </button>
        ))}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No overtime" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 600 }}>
              <thead>
                <tr>
                  {[...(mine ? [] : ["Employee"]), "Day", "Overtime", "Type", "Rate", "Status"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    {mine ? null : <td style={tdStyle}>{o.full_name}</td>}
                    <td style={tdStyle}>{formatDate(o.ot_date)}</td>
                    <td style={tdStyle}>{formatMinutes(o.minutes)}</td>
                    <td style={tdStyle}>{statusLabel(o.category)}</td>
                    <td style={tdStyle}>{Number(o.multiplier)}×</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(o.status)}>{statusLabel(o.status)}</span>
                      {o.compensation === "COMP_OFF" ? <div style={{ fontSize: 11, color: "#6B7280" }}>As comp-off</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </>
  );
}

function OtReport() {
  const to = todayYmd();
  const { data, loading, error, reload } = useLoad(() => hrmsService.getOtReport(addDaysYmd(to, -30), to), []);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {data ? (
        data.people.length === 0 ? (
          <EmptyState title="No overtime in the last 30 days" />
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>
              Last 30 days: {formatMinutes(data.total_approved_minutes)} approved, {formatMinutes(data.total_pending_minutes)} waiting.
            </div>
            <div style={{ ...cardStyle, overflowX: "auto" }}>
              <table className="table mb-0" style={{ minWidth: 560 }}>
                <thead>
                  <tr>
                    {["Employee", "Approved", "Waiting", "Rate-weighted hours", "By type"].map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((p) => (
                    <tr key={p.employee_id}>
                      <td style={tdStyle}>{p.full_name}</td>
                      <td style={tdStyle}>{formatMinutes(p.approved_minutes)}</td>
                      <td style={tdStyle}>{formatMinutes(p.pending_minutes)}</td>
                      <td style={tdStyle}>{p.weighted_hours}</td>
                      <td style={tdStyle}>{Object.entries(p.by_category).map(([k, v]) => `${statusLabel(k)} ${formatMinutes(v)}`).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}
    </LoadState>
  );
}

export default function OvertimeTab({ isManager, isEmployee }) {
  const policy = useLoad(() => hrmsService.getOtPolicy(), []);
  const [tab, setTab] = useState(isEmployee ? "mine" : "team");
  const [asking, setAsking] = useState(false);
  const pre = policy.data && policy.data.approval_mode === "PRE";
  return (
    <>
      {policy.data === null && !policy.loading ? <div style={{ background: "#FEF3C7", color: "#92400E", padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>Overtime is not set up for your company yet, so none is being recorded.</div> : null}
      {pre && isEmployee ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setAsking(true)}>Ask for overtime</PrimaryButton>
        </div>
      ) : null}
      <Tabs tabs={[...(isEmployee ? [{ key: "mine", label: "My overtime" }] : []), ...(isManager ? [{ key: "team", label: "Team" }, { key: "report", label: "Report" }] : [])]} active={tab} onChange={setTab} />
      {tab === "mine" ? <OtList mine /> : null}
      {tab === "team" ? <OtList mine={false} /> : null}
      {tab === "report" ? <OtReport /> : null}
      <FormDrawer
        show={asking}
        title="Ask for overtime"
        initial={{ ot_date: todayYmd(), minutes: 60 }}
        fields={[
          { key: "ot_date", label: "Day", type: "date", required: true },
          { key: "minutes", label: "Minutes", type: "number", required: true, min: 15, hint: "You are paid only for what you actually work, up to this." },
          { key: "reason", label: "Reason", type: "textarea", required: true, max: 500, rows: 3 },
        ]}
        onHide={() => setAsking(false)}
        onSave={(clean) => hrmsService.requestOvertime({ ...clean, minutes: Number(clean.minutes) })}
        submitLabel="Send for approval"
      />
    </>
  );
}
